import Constants from '@/Constants';
import { Answer } from '@/models/Question';
import http from '@/services/HttpService';
import { call, put, select } from 'redux-saga/effects';
import { PayloadAction } from 'typesafe-actions';
import { ApplicationState, store } from '../index';
import { updateCourseStudentViewRequest } from '../list/action';
import {
    finishQuestionnaireFailure,
    finishQuestionnaireSuccess,
    generateQuestionnaireFailure,
    getQuestionClear,
    getQuestionFailure,
    getQuestionnaireFailure,
    getQuestionnaireResponseSummaryFailure,
    getQuestionnaireResponseSummarySuccess,
    getQuestionnaireSuccess,
    getQuestionSuccess,
    listQuestionnairesFailure,
    listQuestionnairesSuccess,
    startQuestionnaireFailure,
    startQuestionnaireRequest,
    startQuestionnaireSuccess,
    updateAnswerFailure,
    updateAnswerSuccess,
} from './action';
import { QuestionnaireTypes } from './types';

export function* listQuestionnairesRequest(
    action: PayloadAction<QuestionnaireTypes.LIST_QUESTIONNAIRES_REQUEST, { classEnrollmentId: number; evaluationId: number; personId: number }>,
) {
    const { classEnrollmentId, evaluationId, personId } = action.payload;
    const url = `${Constants.EVALUATION_QUESTIONNAIRES_BY_ID_URI.replace(
        '%e',
        evaluationId.toString(),
    )}?dynamicAttributes={"path": "classEnrollmentId", "value": "${classEnrollmentId}"}&personId=${personId}`;

    try {
        const { data } = yield call(http.get, url);
        yield put(listQuestionnairesSuccess(data));
    } catch (err) {
        yield put(listQuestionnairesFailure(err));
    }
}

export function* generateQuestionnaire(
    action: PayloadAction<
        QuestionnaireTypes.GENERATE_QUESTIONNAIRE_REQUEST,
        { evaluationId: number; lessonId: number; attemptNumber: number; classEnrollmentId: number }
    >,
) {
    const {
        identity: {
            data: {
                user: { personId },
            },
        },
    } = store.getState();
    try {
        //Generate questionnaire
        const { evaluationId, lessonId, attemptNumber, classEnrollmentId } = action.payload;
        const { data } = yield call(http.post, `${Constants.EVALUATION_QUESTIONNAIRES_RESOURCE}?entityType=lesson&entityId=${lessonId}`, {
            evaluation: {
                id: evaluationId,
            },
            personId: personId,
            evaluatedEntityType: 'lesson',
            evaluatedEntityId: lessonId,
            attemptNumber: attemptNumber,
            dynamicAttributes: {
                classEnrollmentId: classEnrollmentId,
            },
        });

        //Start questionnaire
        yield put(startQuestionnaireRequest(data.id));
    } catch (err) {
        yield put(generateQuestionnaireFailure(err));
    }
}

export function* startQuestionnaire(action: PayloadAction<QuestionnaireTypes.START_QUESTIONNAIRE_REQUEST, number>) {
    try {
        const path = `${Constants.EVALUATION_QUESTIONNAIRES_RESOURCE}/${action.payload}/start`;
        const { data, headers } = yield call(http.post, path);
        data.serverCurrentDate = new Date(headers.currentdate).getTime();
        yield put(startQuestionnaireSuccess(data));
    } catch (error) {
        yield put(startQuestionnaireFailure(error));
    }
}

export function* getQuestionnaire(action: PayloadAction<QuestionnaireTypes.GET_QUESTIONNAIRE_REQUEST, number>) {
    try {
        const { data, headers } = yield call(http.get, `${Constants.EVALUATION_QUESTIONNAIRES_RESOURCE}/${action.payload}`);
        data.serverCurrentDate = new Date(headers.currentdate).getTime();
        yield put(getQuestionnaireSuccess(data));
    } catch (err) {
        yield put(getQuestionnaireFailure(err));
    }
}

export function* getQuestion(action: PayloadAction<QuestionnaireTypes.GET_QUESTION_REQUEST, { questionnaireId: number; questionId: number }>) {
    try {
        const { questionnaireId, questionId } = action.payload;
        const { data } = yield call(http.get, `${Constants.EVALUATION_QUESTIONNAIRES_RESOURCE}/${questionnaireId}/questions/${questionId}`);

        yield put(getQuestionSuccess(data));
    } catch (err) {
        yield put(getQuestionFailure(err));
    }
}

export function* updateAnswer(action: PayloadAction<QuestionnaireTypes.UPDATE_ANSWER_REQUEST, { questionnaireId: number; answer: Answer }>) {
    try {
        const { questionnaireId, answer } = action.payload;
        yield call(http.put, `${Constants.EVALUATION_QUESTIONNAIRES_RESOURCE}/${questionnaireId}/answers/${answer.id}`, answer);

        yield put(updateAnswerSuccess(answer));
    } catch (err) {
        yield put(updateAnswerFailure(err));
    }
}

export function* finishQuestionnaire(action: PayloadAction<QuestionnaireTypes.FINISH_QUESTIONNAIRE_REQUEST, number>) {
    try {
        yield call(http.post, `${Constants.EVALUATION_QUESTIONNAIRES_RESOURCE}/${action.payload}/finish`);

        yield put(getQuestionClear());
        yield put(finishQuestionnaireSuccess());

        const { personId, courseId, classEnrollmentId } = yield select((state: ApplicationState) => ({
            personId: state.questionnaire.questionnnaire.personId,
            courseId: state.lists.currentData.course.id,
            classEnrollmentId: state.questionnaire.questionnnaire.dynamicAttributes.classEnrollmentId,
        }));

        yield put(updateCourseStudentViewRequest({ personId, courseId, classEnrollmentId }));
    } catch (err) {
        yield put(finishQuestionnaireFailure(err));
    }
}

export function* getQuestionnaireResponseSummary(action: PayloadAction<QuestionnaireTypes.GET_QUESTIONNAIRE_RESULT_REQUEST, number>) {
    const TIMES_TO_RETRY = 2;
    let success = false;
    for (let timesAttempted = 0; !success && timesAttempted < TIMES_TO_RETRY; timesAttempted++) {
        try {
            const { data } = yield call(http.get, `${Constants.EVALUATION_QUESTIONNAIRES_RESOURCE}/${action.payload}/result?onlyQuestions=true`);
            yield put(getQuestionnaireResponseSummarySuccess(data));
            success = true;
        } catch (err) {
            if (timesAttempted >= TIMES_TO_RETRY) {
                yield put(getQuestionnaireResponseSummaryFailure(err));
            }
        }
    }
}
